/**
 * The rules behind TimeInput, kept apart from the component so they can be read
 * and tested as rules. Rendering the panel in jsdom costs seconds per case; the
 * arithmetic below is where the behaviour actually lives.
 */

export const HHMM_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

export const pad = (n: number) => String(n).padStart(2, "0");

/** "18:30" -> "6:30 PM". For display beside a 24-hour value, never for storage. */
export function readable(hhmm: string): string {
  const h = Number(hhmm.slice(0, 2));
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${hhmm.slice(3)} ${h < 12 ? "AM" : "PM"}`;
}

/**
 * What someone typed, as a time -- or null if it cannot be read as one.
 *
 * Deliberately generous: "9", "930", "9.30" and "6pm" all land somewhere
 * sensible, because the alternative is a field that rejects what a person would
 * write on paper and makes them reach for the mouse.
 */
export function parseTyped(raw: string): string | null {
  const t = raw.trim().toLowerCase();
  if (!t) return null;
  const pm = /p/.test(t);
  const am = /a/.test(t);
  const digits = t.replace(/\D/g, "");
  if (!digits) return null;

  let h: number;
  let m: number;
  if (/[:.]/.test(t)) {
    const [a = "", b = "0"] = t.split(/[:.]/, 2);
    h = Number(a.replace(/\D/g, ""));
    m = Number(b.replace(/\D/g, "").slice(0, 2) || "0");
  } else if (digits.length <= 2) {
    h = Number(digits);
    m = 0;
  } else if (digits.length === 3) {
    h = Number(digits.slice(0, 1));
    m = Number(digits.slice(1));
  } else if (digits.length === 4) {
    h = Number(digits.slice(0, 2));
    m = Number(digits.slice(2));
  } else {
    return null;
  }

  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  if (pm && h < 12) h += 12;
  if (am && h === 12) h = 0;
  if (h > 23 || m > 59) return null;
  return `${pad(h)}:${pad(m)}`;
}

export interface Range {
  min?: string | undefined;
  max?: string | undefined;
}

export const inRange = (hhmm: string, { min, max }: Range) =>
  (!min || hhmm >= min) && (!max || hhmm <= max);

/**
 * Whether a band of times -- an hour, or half of the day -- has any minute in
 * range. Disabling 09 because 09:00 is one minute too early would hide 09:30,
 * which is in range and is what the person came to pick.
 */
export const bandInRange = (first: string, last: string, { min, max }: Range) =>
  (!min || last >= min) && (!max || first <= max);

/** A composed time pulled inside the range the caller allows. */
export const clampTo = (hhmm: string, { min, max }: Range) =>
  min && hhmm < min ? min : max && hhmm > max ? max : hhmm;

/**
 * The minutes the panel offers. A value already off the step (09:07 from an
 * import, say) keeps its own row, or opening the panel would offer no way back
 * to the time that is already set.
 */
export function minuteOptions(step: number, current: string | null): number[] {
  const list: number[] = [];
  for (let m = 0; m < 60; m += Math.max(1, step)) list.push(m);
  const own = current ? Number(current.slice(3)) : null;
  if (own !== null && !list.includes(own)) list.push(own);
  return list.sort((a, b) => a - b);
}
